import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CitySelector from "../../components/dropdowns/CitySelector";
import CreateCompanyModal from "./Modals/CreateCompanyModal";
import { useAuth } from "../../contexts/AuthContext";
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilIcon,
  XMarkIcon,
  CheckCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import EditCompanyModal from "./Modals/EditCompanyModal";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";

export default function RepresentativeCustomersReservation() {
  const { t } = useTranslation();
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [myReservations, setMyReservations] = useState([]);
  const [cityList, setCityList] = useState([]);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState(null);

  // Pagination pour les compagnies
  const [currentPageCompanies, setCurrentPageCompanies] = useState(1);
  const itemsPerPageCompanies = 10;

  // Pagination pour les réservations
  const [currentPageReservations, setCurrentPageReservations] = useState(1);
  const itemsPerPageReservations = 10;

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!selectedCityId) return;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact?cityId=${selectedCityId}`
        );
        const data = await res.json();
        setCompanies(data);
        setCurrentPageCompanies(1); // Reset à la page 1 lors du changement de ville
      } catch (err) {
        console.error("Erreur chargement compagnies", err);
      }
    };
    fetchCompanies();
  }, [selectedCityId]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await fetch("/geojson/simplified_cities.json");
        const data = await res.json();
        const sorted = data
          .filter((c) => c.nameFr || c.nameEn)
          .sort((a, b) =>
            (a.nameFr || a.nameEn).localeCompare(b.nameFr || b.nameEn)
          );
        setCityList(sorted);
      } catch (err) {
        console.error("Erreur chargement des villes", err);
      }
    };
    fetchCities();
  }, []);

  const getCityNameById = (id) => {
    const city = cityList.find((c) => c.id === id);
    return city?.nameFr || city?.nameEn || "-";
  };

  const handleEdit = (company) => {
    setCompanyToEdit(company);
    setIsEditModalOpen(true);
  };

  const handleCompanyUpdated = (updatedCompany) => {
    setCompanies((prev) =>
      prev.map((c) => (c.id === updatedCompany.id ? updatedCompany : c))
    );
    setMyReservations((prev) =>
      prev.map((c) => (c.id === updatedCompany.id ? updatedCompany : c))
    );
  };

  const handleUnassign = async (company) => {
    const confirm = window.confirm(
      `Retirer la réservation pour "${company.name}" ?`
    );
    if (!confirm) return;

    try {
      const payload = {
        name: company.name,
        cityId: company.cityId,
        createdBy: company.createdBy,
        assignedTo: null,
        note: company.note,
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact/${company.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Erreur lors du retrait");

      // update liste gauche
      setCompanies((prev) =>
        prev.map((c) => (c.id === company.id ? { ...c, assignedTo: null } : c))
      );

      // update liste droite (retirer la réservation)
      setMyReservations((prev) => prev.filter((c) => c.id !== company.id));
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    const fetchMyReservations = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact/MyReservations?userId=${currentUser?.id}`
        );
        const data = await res.json();
        setMyReservations(data);
      } catch (err) {
        console.error("Erreur chargement de mes réservations", err);
      }
    };
    if (currentUser?.id) {
      fetchMyReservations();
    }
  }, [currentUser?.id]);

  const markAsDone = async (id) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact/${id}/complete`,
        { method: "PUT" }
      );
      if (!res.ok) throw new Error("Erreur lors du marquage comme fait");
      setMyReservations((prev) => prev.filter((c) => c.id !== id));
      setCompanies((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReserve = async (company) => {
    const confirm = window.confirm(
      `${t("reservation.confirmReserve")} "${company.name}" ?`
    );
    if (!confirm) return;
    try {
      const payload = {
        name: company.name,
        cityId: company.cityId,
        createdBy: company.createdBy,
        assignedTo: currentUser?.id,
        note: company.note,
      };
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact/${company.id}/reserve`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error("Erreur lors de la réservation");

      // update liste gauche
      setCompanies((prev) =>
        prev.map((c) =>
          c.id === company.id ? { ...c, assignedTo: currentUser?.id } : c
        )
      );

      // update liste droite
      setMyReservations((prev) => [
        ...prev,
        { ...company, assignedTo: currentUser?.id },
      ]);
    } catch (err) {
      alert(err.message);
    }
  };

  // Filtrer et paginer les compagnies
  const filteredCompanies = companies.filter((company) =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPagesCompanies = Math.ceil(
    filteredCompanies.length / itemsPerPageCompanies
  );
  const paginatedCompanies = filteredCompanies.slice(
    (currentPageCompanies - 1) * itemsPerPageCompanies,
    currentPageCompanies * itemsPerPageCompanies
  );

  // Paginer les réservations
  const totalPagesReservations = Math.ceil(
    myReservations.length / itemsPerPageReservations
  );
  const paginatedReservations = myReservations.slice(
    (currentPageReservations - 1) * itemsPerPageReservations,
    currentPageReservations * itemsPerPageReservations
  );

  // Reset la recherche à la page 1
  useEffect(() => {
    setCurrentPageCompanies(1);
  }, [searchTerm]);

  return (
    <div className="p-6 text-primary">
      <button
        onClick={() => navigate(-1)}
        className="btn bg-primary text-white rounded-full flex items-center gap-2 m-2"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        {t("global.back")}
      </button>
      <div className="flex flex-col md:flex-row gap-6 h-[calc(100vh-48px)]">
        {/* Colonne gauche : Compagnies disponibles */}
        <div className="w-full md:w-1/2 space-y-4 flex flex-col">
          <div className="flex flex-col md:flex-row items-end justify-between gap-4">
            <CitySelector value={selectedCityId} onChange={setSelectedCityId} />
            <div className="flex items-end gap-2">
              <input
                type="text"
                placeholder={t("global.search")}
                className="input input-bordered w-72"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                onClick={() => setIsModalOpen(true)}
                className="btn bg-primary text-white rounded-full whitespace-nowrap"
              >
                + {t("global.add")}
              </button>
            </div>
          </div>

          {filteredCompanies.length === 0 ? (
            <div className="text-center text-gray-500 mt-4">
              {t("reservation.noCompanies")}
            </div>
          ) : (
            <>
              <div className="border rounded-lg bg-base-100 shadow-sm overflow-x-auto flex-1">
                <table className="table table-zebra w-full text-sm">
                  <thead>
                    <tr>
                      <th>{t("reservation.companyName")}</th>
                      <th>{t("global.note")}</th>
                      <th>{t("reservation.isAssigned")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCompanies.map((company) => (
                      <tr key={company.id}>
                        <td className="font-medium">{company.name}</td>
                        <td className="max-w-xs">
                          {company.note ? (
                            company.note
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td>{company?.assignedTo ? "Réservé" : "-"}</td>
                        <td className="text-right">
                          <div className="flex gap-2 justify-end">
                            {!company.assignedTo && (
                              <button
                                className="bg-primary text-white rounded-full px-4 py-2 whitespace-nowrap"
                                onClick={() => handleReserve(company)}
                              >
                                {t("reservation.reserve")}
                              </button>
                            )}
                            <button
                              onClick={() => handleEdit(company)}
                              className="btn btn-sm btn-circle btn-ghost text-primary"
                              title={t("global.edit")}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Compagnies */}
              {totalPagesCompanies > 1 && (
                <div className="flex items-center justify-between px-4 py-2 bg-base-100 border-t">
                  <div className="text-sm text-gray-600">
                    {(currentPageCompanies - 1) * itemsPerPageCompanies + 1} -{" "}
                    {Math.min(
                      currentPageCompanies * itemsPerPageCompanies,
                      filteredCompanies.length
                    )}{" "}
                    sur {filteredCompanies.length}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setCurrentPageCompanies((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPageCompanies === 1}
                      className="btn btn-sm btn-circle"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <span className="flex items-center px-4">
                      Page {currentPageCompanies} / {totalPagesCompanies}
                    </span>
                    <button
                      onClick={() =>
                        setCurrentPageCompanies((prev) =>
                          Math.min(totalPagesCompanies, prev + 1)
                        )
                      }
                      disabled={currentPageCompanies === totalPagesCompanies}
                      className="btn btn-sm btn-circle"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Séparateur vertical */}
        <div className="hidden md:block w-px bg-gray-300" />

        {/* Colonne droite : Mes réservations */}
        <div className="w-full md:w-1/2 space-y-4 flex flex-col">
          <h2 className="text-lg font-semibold">
            {t("reservation.myReservations")}
          </h2>
          {myReservations.length === 0 ? (
            <div className="text-gray-500">{t("reservation.none")}</div>
          ) : (
            <>
              <div className="border rounded-lg bg-base-100 shadow-sm overflow-x-auto flex-1">
                <table className="table table-zebra w-full text-sm">
                  <thead>
                    <tr>
                      <th>{t("reservation.companyName")}</th>
                      <th>{t("global.note")}</th>
                      <th>{t("global.city")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedReservations.map((r) => (
                      <tr key={r.id}>
                        <td className="font-medium">{r.name}</td>
                        <td className="max-w-xs">
                          {r.note ? (
                            r.note
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td>{getCityNameById(r.cityId)}</td>
                        <td className="text-right">
                          <div className="flex gap-2 justify-end">
                            {r.isDone ? (
                              <span className="text-xs italic ">
                                {t("reservation.completed")}{" "}
                                <ShieldCheckIcon className="w-4 h-4 inline-block ml-1" />
                              </span>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEdit(r)}
                                  className="btn btn-sm btn-circle btn-ghost text-primary flex items-center justify-center"
                                  title={t("global.edit")}
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => markAsDone(r.id)}
                                  className="btn btn-sm btn-ghost btn-circle flex items-center justify-center"
                                  title={t("reservation.markDone")}
                                >
                                  <CheckCircleIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleUnassign(r)}
                                  className="btn btn-sm btn-circle btn-ghost text-red-500 flex items-center justify-center"
                                  title={t("reservation.removeReservation")}
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Réservations */}
              {totalPagesReservations > 1 && (
                <div className="flex items-center justify-between px-4 py-2 bg-base-100 border-t">
                  <div className="text-sm text-gray-600">
                    {(currentPageReservations - 1) * itemsPerPageReservations +
                      1}{" "}
                    -{" "}
                    {Math.min(
                      currentPageReservations * itemsPerPageReservations,
                      myReservations.length
                    )}{" "}
                    sur {myReservations.length}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setCurrentPageReservations((prev) =>
                          Math.max(1, prev - 1)
                        )
                      }
                      disabled={currentPageReservations === 1}
                      className="btn btn-sm btn-circle"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <span className="flex items-center px-4">
                      Page {currentPageReservations} / {totalPagesReservations}
                    </span>
                    <button
                      onClick={() =>
                        setCurrentPageReservations((prev) =>
                          Math.min(totalPagesReservations, prev + 1)
                        )
                      }
                      disabled={
                        currentPageReservations === totalPagesReservations
                      }
                      className="btn btn-sm btn-circle"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <CreateCompanyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        cityId={selectedCityId}
        cityIdChanged={setSelectedCityId}
        onCompanyCreated={(company) => {
          setCompanies((prev) => [company, ...prev]);
          if (company.assignedTo === currentUser?.id) {
            setMyReservations((prev) => [company, ...prev]);
          }
        }}
      />
      <EditCompanyModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setCompanyToEdit(null);
        }}
        company={companyToEdit}
        onCompanyUpdated={handleCompanyUpdated}
      />
    </div>
  );
}
